'use client';

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { getCookie, setCookie } from 'cookies-next';
import {
  resolveLanguage,
  VALID_LANGUAGES,
  DEFAULT_LANGUAGE,
  ValidLanguage,
} from '@/utils/languageResolver';
import { logger } from '@/utils/logger';

// Define the context type
interface LanguageContextType {
  currentLang: ValidLanguage;
  setCurrentLang: (lang: ValidLanguage) => void;
}

const LanguageContext = createContext<LanguageContextType>({
  currentLang: DEFAULT_LANGUAGE,
  setCurrentLang: () => {},
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
  initialLang?: ValidLanguage;
}> = ({ children, initialLang = DEFAULT_LANGUAGE }) => {
  const [currentLang, setCurrentLang] = useState<ValidLanguage>(initialLang);
  const pathname = usePathname();
  const router = useRouter();
  const isNavigating = useRef(false);

  // Set language in both document and storage
  const updateLanguageAttributes = (lang: ValidLanguage) => {
    // Update meta tag for language
    const metaLang = document.querySelector('meta[name="language"]');
    if (metaLang) {
      metaLang.setAttribute('content', lang);
    } else {
      // Create meta tag if it doesn't exist
      const newMetaLang = document.createElement('meta');
      newMetaLang.setAttribute('name', 'language');
      newMetaLang.setAttribute('content', lang);
      document.head.appendChild(newMetaLang);
    }

    // Update document language attribute directly
    document.documentElement.lang = lang;

    // Store language in localStorage for persistence
    try {
      localStorage.setItem('preferredLanguage', lang);
    } catch (e) {
      logger.warn('Could not save language to localStorage:', e);
    }

    // Store language in cookie
    setCookie('NEXT_LOCALE', lang);

    // Log the language change
    logger.log('Language updated to:', lang);
  };

  // This effect runs on component mount to set initial language
  useEffect(() => {
    if (initialLang && VALID_LANGUAGES.includes(initialLang)) {
      setCurrentLang(initialLang);
      updateLanguageAttributes(initialLang);
    }
  }, [initialLang]);

  // This effect runs whenever the pathname changes
  useEffect(() => {
    // If we're already in the middle of a navigation, don't re-trigger
    if (isNavigating.current) return;

    // Extract language from the URL path
    const pathParts = pathname.split('/').filter(Boolean); // Remove empty strings
    const urlLang = pathParts[0] || '';

    // Resolve language with our utility
    const resolvedLang = resolveLanguage({
      pathLanguage: urlLang,
      cookies:
        typeof document !== 'undefined'
          ? { NEXT_LOCALE: getCookie('NEXT_LOCALE') as string }
          : undefined,
      localStorage: typeof window !== 'undefined' ? localStorage : undefined,
    });

    // If the language is different, update it
    if (resolvedLang !== currentLang) {
      setCurrentLang(resolvedLang);
      updateLanguageAttributes(resolvedLang);

      // If URL doesn't match the resolved language, redirect
      if (urlLang !== resolvedLang) {
        // If we need to redirect, set the flag to prevent double navigation
        isNavigating.current = true;

        // Check if the pathname already contains a language prefix
        const hasLanguagePrefix = pathParts.some((part) =>
          VALID_LANGUAGES.includes(part as ValidLanguage)
        );

        // Only add language prefix if it doesn't already exist
        const newPathname = hasLanguagePrefix
          ? pathname.replace(`/${urlLang}`, `/${resolvedLang}`)
          : `/${resolvedLang}${pathname}`;

        router.push(newPathname);

        // Reset the flag after navigation completes
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
    }
  }, [pathname, router, currentLang]);

  const handleSetCurrentLang = (lang: ValidLanguage) => {
    if (!VALID_LANGUAGES.includes(lang)) {
      logger.warn(`Attempted to set invalid language: ${lang}`);
      return;
    }

    // If we're already changing to this language, don't trigger again
    if (lang === currentLang || isNavigating.current) return;

    // Set the flag to prevent double navigation
    isNavigating.current = true;

    // Update state and storage
    setCurrentLang(lang);
    updateLanguageAttributes(lang);

    // Update the URL when language is changed programmatically
    const pathParts = pathname.split('/').filter(Boolean); // Remove empty strings
    let newPathname: string;

    // Check if any part of the path is a valid language
    const existingLangIndex = pathParts.findIndex((part) =>
      VALID_LANGUAGES.includes(part as ValidLanguage)
    );

    if (existingLangIndex !== -1) {
      // Replace the existing language segment
      pathParts[existingLangIndex] = lang;
      newPathname = '/' + pathParts.join('/');
    } else {
      // Add the language segment if it doesn't exist
      newPathname = `/${lang}${pathname}`;
    }

    router.push(newPathname);

    // Reset the flag after navigation completes
    setTimeout(() => {
      isNavigating.current = false;
    }, 100);
  };

  return (
    <LanguageContext.Provider value={{ currentLang, setCurrentLang: handleSetCurrentLang }}>
      {children}
    </LanguageContext.Provider>
  );
};
